/**
 * Who may mark an event COMPLETED ("closed"). Owner decision 2026-09-30: admins and superadmins only.
 * The database enforces the same rule (migration 2026-09-30-012, trigger events_require_admin_to_complete),
 * so this only decides what the UI offers. Role spellings vary ('superadmin' in the database, 'super_admin' in
 * the router), so compare with underscores removed.
 */
export function canMarkEventCompleted(
  role: string | null | undefined,
): boolean {
  const r = (role ?? "").toLowerCase().replace(/_/g, "");
  return r === "admin" || r === "superadmin";
}

/**
 * Whether the "Completed" option should be selectable in a status picker. A non-admin looking at an event that
 * is ALREADY completed still sees the option (so the current value displays), but it is disabled; choosing it
 * from any other status is never offered to them.
 */
export function completedOptionState(
  role: string | null | undefined,
  currentStatus: string | null | undefined,
): "enabled" | "shown_disabled" | "hidden" {
  if (canMarkEventCompleted(role)) {
    return "enabled";
  }
  return currentStatus === "completed" ? "shown_disabled" : "hidden";
}

/** Operator-facing text when the database refused a completion (EVENT_COMPLETE_ADMIN_ONLY, MSR_EVENT_DAYS_NOT_CLOSED); null otherwise. */
export function completionRefusalMessage(error: unknown): string | null {
  const msg =
    typeof error === "object" && error && "message" in error
      ? String((error as { message: unknown }).message)
      : String(error ?? "");
  if (msg.includes("EVENT_COMPLETE_ADMIN_ONLY")) {
    return "Only an admin or superadmin can mark an event completed.";
  }
  // Migration 505: an event started and closed per day completes only once every tracked day is closed. The server
  // sentence names the open days; show it without its code.
  const m = /MSR_EVENT_DAYS_NOT_CLOSED:\s*(.*)$/s.exec(msg);
  return m ? m[1] : null;
}

/**
 * Whether saving attendance on the close-rate card should ALSO mark the event completed (SA-2).
 *
 * Only for a user who may complete (admin/superadmin). For anyone else the combined update
 * `{attendance_count, status:'completed'}` is refused as a whole by the admin-only trigger
 * (EVENT_COMPLETE_ADMIN_ONLY), so the attendance itself was never saved — the MSR-plan slice-0 bug.
 * Non-admins therefore save attendance alone; an admin completes the event later.
 *
 * `eligible` is the event-side rule the card already applied (attendance-tracked type, a real value, not
 * future-dated, not already completed/cancelled).
 */
export function attendanceSaveCompletes(
  eligible: boolean,
  role: string | null | undefined,
): { complete: boolean; adminNeededToComplete: boolean } {
  if (!eligible) {
    return { complete: false, adminNeededToComplete: false };
  }
  return canMarkEventCompleted(role)
    ? { complete: true, adminNeededToComplete: false }
    : { complete: false, adminNeededToComplete: true };
}
